import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Lock, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useSale, salePrice, saleAppliesTo } from '../utils/sale';
import { shippingEstimate } from '../utils/shipping';
import { imgUrl } from '../utils/imgUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// We do not ship to Alaska (AK) or Hawaii (HI) — excluded from the selector below.
const US_STATES = [
  ['AL', 'Alabama'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'], ['CO', 'Colorado'],
  ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'],
  ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'],
  ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'],
  ['MN', 'Minnesota'], ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
  ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'],
  ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'],
  ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];
const NO_SHIP_MESSAGE = 'We currently do not ship to Alaska or Hawaii. Please contact support for a custom shipping quote.';
const isBlockedDestination = (state, zip) => {
  const s = (state || '').trim().toUpperCase();
  const z3 = (zip || '').trim().slice(0, 3);
  return ['AK', 'HI', 'ALASKA', 'HAWAII'].includes(s) || ['995', '996', '997', '998', '999', '967', '968'].includes(z3);
};

const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Inner form rendered inside <Elements>; owns the Stripe card UI + pay action. */
const PaymentForm = ({ amount, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [walletsReady, setWalletsReady] = useState(false);

  const confirmNow = async () => {
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/checkout` },
      redirect: 'if_required',
    });
    if (confirmError) {
      setError(confirmError.message || 'Payment failed. Please try again.');
      return false;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      await onPaid(paymentIntent.id);
    }
    return true;
  };

  // Google Pay / Apple Pay / Link express buttons
  const handleExpressReady = ({ availablePaymentMethods }) => {
    setWalletsReady(!!availablePaymentMethods);
  };
  const handleExpressConfirm = async () => {
    if (!stripe || !elements) return;
    setError('');
    setProcessing(true);
    await confirmNow();
    setProcessing(false);
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');
    await confirmNow();
    setProcessing(false);
  };

  return (
    <form onSubmit={handlePay} className="space-y-6" data-testid="stripe-payment-form">
      {/* Express wallet buttons (Google Pay / Apple Pay / Link) — only render on supported devices/browsers */}
      <div data-testid="express-checkout-wallets">
        <ExpressCheckoutElement
          onReady={handleExpressReady}
          onConfirm={handleExpressConfirm}
          options={{ layout: { maxColumns: 1, maxRows: 3 } }}
        />
      </div>
      {walletsReady && (
        <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-gray-400">
          <span className="flex-1 h-px bg-gray-200" />
          Or pay with card
          <span className="flex-1 h-px bg-gray-200" />
        </div>
      )}
      <PaymentElement options={{ layout: { type: 'accordion', defaultCollapsed: false, radios: 'always', spacedAccordionItems: true, visibleAccordionItemsCount: 0 } }} />
      {error && (
        <p className="text-sm text-red-600" data-testid="payment-error">{error}</p>
      )}
      <Button
        type="submit"
        disabled={!stripe || processing}
        className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg font-semibold rounded-xl transition-all duration-300 hover:shadow-xl"
        data-testid="pay-now-button"
      >
        {processing ? (
          <span className="flex items-center justify-center space-x-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Processing…</span>
          </span>
        ) : (
          `Pay ${fmt(amount)}`
        )}
      </Button>
      <p className="flex items-center justify-center text-xs text-gray-500">
        <Lock size={13} className="mr-1" /> Payments are encrypted &amp; processed securely by Stripe
      </p>
    </form>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const sale = useSale();
  const onSale = cartItems.some((item) => saleAppliesTo(sale, item));
  const saleSubtotal = cartItems.reduce((t, item) => t + salePrice(item.price, sale, item) * item.quantity, 0);
  const [phase, setPhase] = useState('shipping'); // 'shipping' | 'payment'
  const [starting, setStarting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [serverTotal, setServerTotal] = useState(0);
  const [shipError, setShipError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', state: '', zipCode: '',
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const finalizePayment = useCallback(async (paymentIntentId) => {
    try {
      const res = await axios.post(`${API}/payments/confirm`, { paymentIntentId });
      if (res.data.paid) {
        setCompleted(true);
        clearCart();
        toast.success('Payment successful!', { description: 'A confirmation email is on its way.' });
        navigate('/order-success', { state: { orderId: res.data.orderId, email: res.data.email, country: res.data.country || 'US' } });
      } else {
        toast.error('Payment not completed', { description: `Status: ${res.data.status || 'unknown'}` });
      }
    } catch (err) {
      toast.error('Could not verify payment', { description: 'Please contact support if you were charged.' });
    }
  }, [clearCart, navigate]);

  // Handle return from a redirect-based authentication (e.g. 3D Secure test cards)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pi = params.get('payment_intent');
    const redirectStatus = params.get('redirect_status');
    if (pi && redirectStatus === 'succeeded') {
      finalizePayment(pi);
    }
  }, [finalizePayment]);

  // Send shoppers with an empty cart back (unless an order just completed)
  useEffect(() => {
    const returning = new URLSearchParams(window.location.search).get('payment_intent');
    if (cartItems.length === 0 && !completed && !returning) {
      navigate('/cart');
    }
  }, [cartItems.length, completed, navigate]);

  const startPayment = async (e) => {
    e.preventDefault();
    if (isBlockedDestination(formData.state, formData.zipCode)) {
      setShipError(NO_SHIP_MESSAGE);
      toast.error('Shipping not available', { description: NO_SHIP_MESSAGE });
      return;
    }
    setShipError('');
    setStarting(true);
    try {
      const items = cartItems.map((item) => ({
        productId: item.id,
        selectedColor: item.selectedColor,
        customizations: item.customizations || {},
        quantity: item.quantity,
      }));
      const customerInfo = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        shippingAddress: {
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
        },
      };
      const res = await axios.post(`${API}/payments/create-intent`, { customerInfo, items });
      setClientSecret(res.data.clientSecret);
      setServerTotal(res.data.amount);
      setStripePromise(loadStripe(res.data.publishableKey));
      setPhase('payment');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && /alaska|hawaii/i.test(detail)) setShipError(detail);
      toast.error('Could not start checkout', {
        description: detail || 'Please try again.',
      });
    } finally {
      setStarting(false);
    }
  };

  const appearance = {
    theme: 'stripe',
    variables: { colorPrimary: '#0891b2', borderRadius: '10px', fontSizeBase: '16px' },
  };

  const inputClass = 'mt-2 h-12';

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16 sm:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 sm:mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-6">
            {phase === 'shipping' && (
              <form onSubmit={startPayment} className="space-y-6" data-testid="shipping-form">
                <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-lg">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-6">Shipping Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input id="firstName" name="firstName" required value={formData.firstName} onChange={handleChange} className={inputClass} data-testid="checkout-firstName" />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input id="lastName" name="lastName" required value={formData.lastName} onChange={handleChange} className={inputClass} data-testid="checkout-lastName" />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className={inputClass} data-testid="checkout-email" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange} className={inputClass} data-testid="checkout-phone" />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="address">Street Address *</Label>
                      <Input id="address" name="address" required value={formData.address} onChange={handleChange} className={inputClass} data-testid="checkout-address" />
                    </div>
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input id="city" name="city" required value={formData.city} onChange={handleChange} className={inputClass} data-testid="checkout-city" />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <select
                        id="state"
                        name="state"
                        required
                        value={formData.state}
                        onChange={(e) => { handleChange(e); setShipError(''); }}
                        className="mt-2 h-12 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        data-testid="checkout-state"
                      >
                        <option value="">Select a state</option>
                        {US_STATES.map(([code, name]) => (
                          <option key={code} value={code}>{name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="zipCode">ZIP Code *</Label>
                      <Input id="zipCode" name="zipCode" required value={formData.zipCode} onChange={(e) => { handleChange(e); setShipError(''); }} className={inputClass} data-testid="checkout-zipCode" />
                    </div>
                  </div>
                  {shipError && (
                    <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid="shipping-restriction-error">
                      {shipError}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={starting}
                  className="w-full h-14 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg font-semibold rounded-xl transition-all duration-300 hover:shadow-xl"
                  data-testid="continue-to-payment-button"
                >
                  {starting ? 'Starting secure checkout…' : 'Continue to Payment'}
                </Button>
              </form>
            )}

            {phase === 'payment' && (
              <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-lg">
                <div className="flex items-center justify-between mb-5 sm:mb-6 flex-wrap gap-2">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Payment</h2>
                  <button
                    type="button"
                    onClick={() => setPhase('shipping')}
                    className="flex items-center text-sm text-gray-500 hover:text-gray-800"
                    data-testid="back-to-shipping-button"
                  >
                    <ArrowLeft size={15} className="mr-1" /> Edit shipping
                  </button>
                </div>
                {stripePromise && clientSecret ? (
                  <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
                    <PaymentForm amount={serverTotal} onPaid={finalizePayment} />
                  </Elements>
                ) : (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-lg lg:sticky lg:top-28">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-6">Order Summary</h2>
              <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item.cartId} className="flex gap-4 pb-4 border-b border-gray-200">
                    <img
                      src={imgUrl(item.image)}
                      alt={item.name}
                      className="w-20 h-20 object-contain rounded-lg bg-gray-50"
                      onError={(e) => {
                        const fallback = (Array.isArray(item.images) && item.images.find((u) => u && u !== item.image)) || null;
                        const proxied = fallback ? imgUrl(fallback) : null;
                        if (proxied && e.currentTarget.src !== proxied) {
                          e.currentTarget.src = proxied;
                        }
                      }}
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 text-sm line-clamp-2">{item.name}</h4>
                      <p className="text-xs text-gray-600 mt-1">{item.selectedColor}</p>
                      {shippingEstimate(item) && (
                        <p className="text-xs text-cyan-700 font-medium mt-0.5" data-testid={`checkout-ship-note-${item.cartId}`}>
                          {shippingEstimate(item)}
                        </p>
                      )}
                      <p className="text-sm text-gray-900 mt-1">
                        Qty: {item.quantity} × {fmt(salePrice(item.price, sale, item))}
                        {saleAppliesTo(sale, item) && <span className="ml-1 text-gray-400 line-through">{fmt(item.price)}</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="space-y-3 border-t border-gray-200 pt-4">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>{fmt(getCartTotal())}</span>
                </div>
                {onSale && (
                  <div className="flex justify-between text-cyan-600 font-medium" data-testid="checkout-sale-line">
                    <span>Sale discount</span>
                    <span>-{fmt(getCartTotal() - saleSubtotal)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="text-green-600 font-medium">FREE</span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-2xl font-bold text-gray-900">
                    <span>Total</span>
                    <span data-testid="checkout-total">{fmt(saleSubtotal)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
